import { useTranslation } from "react-i18next";
import { Panel, PanelHeaderTemplateOptions } from "primereact/panel";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import { formatarParaMoedaBRL } from "../../../utils";
import NadaConsta from "../../../components/nadaConsta";
import { Score } from "../../../components/Score";

type Registro = Record<string, any>;

const formatDate = (value: Date) => {
  return value.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const parseValor = (valor: any): number => {
  if (!valor) return 0;
  return Number(String(valor).replaceAll(".", "").replace(",", ".")) || 0;
};

const buildResumoCards = (registro: Registro[] | undefined, dataField: string) => {
  if (!registro || registro.length === 0) return [];

  let valorTotal = 0;
  let ultimaData: Date | null = null;

  registro.forEach((item) => {
    valorTotal += parseValor(item.valor);
    const raw = item[dataField];
    if (raw) {
      const [dia, mes, ano] = String(raw).split("/").map((v) => parseInt(v, 10));
      if (dia && mes && ano) {
        const dataItem = new Date(ano, mes - 1, dia);
        if (!ultimaData || ultimaData < dataItem) {
          ultimaData = dataItem;
        }
      }
    }
  });

  return [
    { icon: "pi pi-chart-line", label: "Quantidade Registros", value: registro.length },
    { icon: "pi pi-calendar-clock", label: "Última Ocorrência", value: ultimaData ? formatDate(ultimaData) : "" },
    { icon: "pi pi-dollar", label: "Valor Total", value: formatarParaMoedaBRL(valorTotal) },
  ];
};

const buildChequeSemFundoCards = (registro: Registro[] | undefined) => {
  if (!registro || registro.length === 0) return [];

  let totalCheques = 0;
  let ultimaData: Date | null = null;

  registro.forEach((item) => {
    totalCheques += Number(item.quantidade_cheques) || 0;
    const raw = item.data_da_ocorrencia;
    if (raw) {
      const [dia, mes, ano] = String(raw).split("/").map((v) => parseInt(v, 10));
      if (dia && mes && ano) {
        const dataItem = new Date(ano, mes - 1, dia);
        if (!ultimaData || ultimaData < dataItem) {
          ultimaData = dataItem;
        }
      }
    }
  });

  return [
    { icon: "pi pi-chart-line", label: "Quantidade Registros", value: registro.length },
    { icon: "pi pi-calendar-clock", label: "Última Ocorrência", value: ultimaData ? formatDate(ultimaData) : "" },
    { icon: "pi pi-money-bill", label: "Total de Cheques", value: totalCheques },
  ];
};

const getRange = (pontuacao: number) => {
  if (pontuacao >= 400 && pontuacao < 500) return "REGULAR";
  if (pontuacao >= 500 && pontuacao < 600) return "BOM";
  if (pontuacao >= 600) return "OTIMO";
  return "RUIM";
};

const CardsResumo = ({ cards }: { cards: any[] }) => (
  <div className="flex-consulta flex-wrap gap-3">
    {cards.map((item, index) => (
      <Card className="flex-1" style={{ backgroundColor: "#2e2eb8" }} key={index}>
        <div className="flex-consulta justify-content-between gap-5">
          <div className="flex-consulta flex-column gap-1">
            <span style={{ color: "white" }} className="text-sm">{item.label}</span>
            <span style={{ color: "white" }} className="font-bold text-lg">{item.value}</span>
          </div>
          <span
            className="w-2rem h-2rem border-circle inline-flex justify-content-center align-items-center text-center"
            style={{ backgroundColor: "white", color: "#183462" }}
          >
            <i className={item.icon} />
          </span>
        </div>
      </Card>
    ))}
  </div>
);

export const RestricaoFullResumo = ({ dados }: { dados: any }) => {
  const { t } = useTranslation();

  const resumo = dados?.resumo;
  const pefin = dados?.pefin?.registro || [];
  const refin = dados?.refin?.registro || [];
  const pendenciasVencidas = dados?.pendencias_vencidas?.registro || [];
  const protestos = dados?.protestos?.registro || [];
  const chequeSemFundo = dados?.cheque_sem_fundo?.registro || [];

  const resumos: any[] = [];
  if (resumo) {
    if (resumo.pefin) {
      resumos.push({ resumo: t("pefins"), quantidade: resumo.pefin.quantidade, data: resumo.pefin.data_ultimo, total: resumo.pefin.valor_total || "-" });
    }
    if (resumo.refin) {
      resumos.push({ resumo: t("refins"), quantidade: resumo.refin.quantidade, data: resumo.refin.data_ultimo, total: resumo.refin.valor_total || "-" });
    }
    if (resumo.pendencias_vencidas) {
      resumos.push({ resumo: t("pendencia"), quantidade: resumo.pendencias_vencidas.quantidade, data: resumo.pendencias_vencidas.data_ultimo, total: resumo.pendencias_vencidas.valor_total || "-" });
    }
    if (resumo.protestos) {
      resumos.push({ resumo: t("protestos"), quantidade: resumo.protestos.quantidade, data: resumo.protestos.data_ultimo, total: resumo.protestos.valor_total || "-" });
    }
    if (resumo.cheque_sem_fundo) {
      resumos.push({ resumo: "Cheque sem Fundo", quantidade: resumo.cheque_sem_fundo.quantidade, data: resumo.cheque_sem_fundo.data_ultimo, total: resumo.cheque_sem_fundo.valor_total || "-" });
    }
  }

  const headerTemplate = (label: string, registro: Registro[]) => (options: PanelHeaderTemplateOptions) => {
    const className = `${options.className} `;
    return (
      <div className={className}>
        {registro.length > 0 ? (
          <div>
            <span style={{ color: "#C70039" }} className="pi-consulta pi pi-exclamation-triangle"></span>
            <span className="p-panel-title" style={{ marginLeft: "1rem", fontFamily: "var(--font-family)", color: "black" }}>{label}</span>
          </div>
        ) : (
          <span className="p-panel-title" style={{ marginLeft: "1rem", fontFamily: "var(--font-family)", color: "black" }}>{label}</span>
        )}
      </div>
    );
  };

  return (
    <div>
      {dados?.score && (
        <div>
          <br />
          <Panel header="Score">
            <div style={{ display: "flex", gap: "40px" }}>
              <div className="scrScore">
                <Score score={String(dados.score)} range={getRange(Number(dados.score))} />
              </div>
            </div>
          </Panel>
        </div>
      )}

      {resumos.length > 0 && (
        <div>
          <br />
          <Panel header="Resumo">
            <DataTable size="small" value={resumos} tableStyle={{ minWidth: "50rem" }}>
              <Column field="resumo" header="Resumo"></Column>
              <Column field="quantidade" header={t("quantidade")}></Column>
              <Column field="data" header={t("dataUltimaOcorrencia")}></Column>
              <Column field="total" header="Total"></Column>
            </DataTable>
          </Panel>
        </div>
      )}

      <br />
      <Panel headerTemplate={headerTemplate(t("pefins"), pefin)}>
        {pefin.length === 0 ? (<NadaConsta />) : (<>
          <CardsResumo cards={buildResumoCards(pefin, "data_entrada")} />
          <br />
          <DataTable size="small" value={pefin} tableStyle={{ minWidth: "50rem" }}>
            <Column field="data_entrada" header={t("data")}></Column>
            <Column field="contrato" header="Contrato"></Column>
            <Column field="avalista" header="Avalista"></Column>
            <Column field="informante" header="Informante"></Column>
            <Column field="valor" header={t("valor")}></Column>
            <Column field="tipo_restricao" header={t("tipo")}></Column>
          </DataTable>
        </>)}
      </Panel>

      <br />
      <Panel headerTemplate={headerTemplate(t("refins"), refin)}>
        {refin.length === 0 ? (<NadaConsta />) : (<>
          <CardsResumo cards={buildResumoCards(refin, "data_entrada")} />
          <br />
          <DataTable size="small" value={refin} tableStyle={{ minWidth: "50rem" }}>
            <Column field="data_entrada" header={t("data")}></Column>
            <Column field="contrato" header="Contrato"></Column>
            <Column field="avalista" header="Avalista"></Column>
            <Column field="informante" header="Informante"></Column>
            <Column field="valor" header={t("valor")}></Column>
            <Column field="tipo_restricao" header={t("tipo")}></Column>
          </DataTable>
        </>)}
      </Panel>

      <br />
      <Panel headerTemplate={headerTemplate(t("pendencia"), pendenciasVencidas)}>
        {pendenciasVencidas.length === 0 ? (<NadaConsta />) : (<>
          <CardsResumo cards={buildResumoCards(pendenciasVencidas, "data_entrada")} />
          <br />
          <DataTable size="small" value={pendenciasVencidas} tableStyle={{ minWidth: "50rem" }}>
            <Column field="data_entrada" header={t("data")}></Column>
            <Column field="contrato" header="Contrato"></Column>
            <Column field="tipo_ocorrencia" header={t("tipo")}></Column>
            <Column field="avalista" header="Avalista"></Column>
            <Column field="informante" header="Informante"></Column>
            <Column field="valor" header={t("valor")}></Column>
          </DataTable>
        </>)}
      </Panel>

      <br />
      <Panel headerTemplate={headerTemplate(t("protestos"), protestos)}>
        {protestos.length === 0 ? (<NadaConsta />) : (<>
          <CardsResumo cards={buildResumoCards(protestos, "data_protesto")} />
          <br />
          <DataTable size="small" value={protestos} tableStyle={{ minWidth: "50rem" }}>
            <Column field="data_protesto" header={t("data")}></Column>
            <Column field="cartorio" header={t("cartorio")}></Column>
            <Column field="origem" header={t("cidade")}></Column>
            <Column field="estado" header={t("uf")}></Column>
            <Column field="valor" header={t("valor")}></Column>
            <Column field="descricao" header="Descrição"></Column>
          </DataTable>
        </>)}
      </Panel>

      <br />
      <Panel headerTemplate={headerTemplate("Cheque sem Fundo", chequeSemFundo)}>
        {chequeSemFundo.length === 0 ? (<NadaConsta />) : (<>
          <CardsResumo cards={buildChequeSemFundoCards(chequeSemFundo)} />
          <br />
          <DataTable size="small" value={chequeSemFundo} tableStyle={{ minWidth: "50rem" }}>
            <Column field="data_da_ocorrencia" header={t("data")}></Column>
            <Column field="nome_banco" header="Banco"></Column>
            <Column field="agencia" header="Agência"></Column>
            <Column field="quantidade_cheques" header="Qtde. Cheques"></Column>
          </DataTable>
        </>)}
      </Panel>
    </div>
  );
};

export default RestricaoFullResumo;
